import type { ObservableMap } from "mobx";
import { action, computed, makeObservable, observable } from "mobx";
import type { Timestamp } from "../proto/google/protobuf/timestamp";
import type {
	FlightEnteringAirspaceMessage,
	FlightMilestonePositionMessage,
	NewFlightMessage,
	TargetReportMessage,
} from "../proto/ProtobufAirTrafficSimulator";
import { WakeTurbulenceCategory } from "../proto/ProtobufAirTrafficSimulator";
import { convertMetersToFlightLevel } from "../utils";
import type AircraftInfo from "./AircraftInfo";
import type AircraftType from "./AircraftType";
import convertTimestamp from "./convertTimestamp";
import FlightInSectorModel from "./FlightInSectorModel";
import type FlightRoute from "./FlightRoute";
import type { PredictiveTrajectoryMode } from "./predictiveTrajectoryState";
import { getUpcomingRouteFixes } from "./routeProgress";
import type SectorStore from "./SectorStore";
import type SimulatorStore from "./SimulatorStore";
import type Trajectory from "./Trajectory";

/** Represents the next sector the aircraft will enter, with the trajectory point where it happens */
export interface NextSectorInfo {
	/** The sector ID */
	sectorId: string;
	/** The trajectory point where the aircraft enters the next sector */
	trajectoryPoint: Trajectory;
}

function convertToFlightMeters(alt: number): number {
	const feet = alt * 3.280_84;
	return feet / 100;
}

function convertMSToKnots(speed: number): number {
	return speed * 1.943_844_492_440_6;
}

export default class AircraftModel {
	aircraftId: string;

	assignedFlightId: string;

	callSign: string;

	airlineIcaoCode: string;

	airlineCallSign: string;

	lastKnownLongitude = 0;

	lastKnownLatitude = 0;

	lastKnownAltitude = 0;

	lastKnownBearing = 0;

	lastKnownSpeed = 0;

	lastKnownVerticalSpeed = 0;

	arrivalAirport: string;

	departureAirport: string;

	lastTargetReportTime = 0;

	controlledBy = "OTHER";

	nextSectorController = "NS";

	milestoneTargetTimestamp = 0;

	milestoneTargetObjectId: string | undefined;

	lastPassedMilestoneTimestamp = 0;

	lastPassedMilestoneObjectId: string | undefined;

	assignedFlightLevel = "FL.S";

	assignedBearing: number | undefined;

	assignedSpeed: number | undefined;

	predictiveTrajectoryMode: PredictiveTrajectoryMode = "unset";

	predictiveTrajectoryWaypointId: string | undefined;

	predictiveTrajectoryWaypointLatitude: number | undefined;

	predictiveTrajectoryWaypointLongitude: number | undefined;

	/** Explicitly set next sector flight level (raw value), or undefined if derived from trajectory */
	manualNextSectorFL: number | undefined = undefined;

	nextACCFL = "";

	/** Whether the nextACCFL value was manually set through the interface */
	isNextACCFLManuallySet = false;

	/** Whether the nextACCFL value is currently flashing (recently changed) */
	isNextACCFLFlashing = false;

	/** Timeout handle for clearing the flash state (not observable) */
	private nextACCFLFlashTimeout: ReturnType<typeof setTimeout> | null = null;

	/** Whether the nextSectorFL value is currently flashing (recently changed) */
	isNextSectorFLFlashing = false;

	/** Timeout handle for clearing the flash state (not observable) */
	private nextSectorFLFlashTimeout: ReturnType<typeof setTimeout> | null = null;

	localAssignedFlightLevel = "";

	aircraftInfo: ObservableMap<string, AircraftInfo>;

	aircraftTypes: ObservableMap<string, AircraftType>;

	flightRoutes: ObservableMap<string, FlightRoute>;

	sectorStore: SectorStore;

	flightInSectorTimes: ObservableMap<string, FlightInSectorModel> =
		observable.map(undefined, { deep: false });

	simulatorStore: SimulatorStore;

	positionHistory: Array<{ lat: number; lon: number }> = [];

	transponderCode = "";

	currentTime = "";

	routeWaypoints: Array<{ sector: string; level: string }> = [];

	frequency = "";

	/** Whether this aircraft has CPDLC (Controller-Pilot Data Link Communications) capability. Assigned by AircraftStore based on alphabetical callSign ordering. */
	hasCPDLC = false;

	/**
	 * @deprecated Use AircraftStore.getRequestsForAircraft() instead.
	 * This field is kept for backward compatibility and will store the most recent flight level request parameter.
	 */
	flightLevelPilotRequest: string | boolean = false;

	/**
	 * @deprecated Use AircraftStore.getRequestsForAircraft() instead.
	 * This field is kept for backward compatibility and will store the most recent bearing request parameter.
	 */
	bearingPilotRequest: string | boolean = false;

	/** Whether the aircraft has been "degreased" (acknowledged). Once true, popup text is no longer bold. */
	degreased = false;

	constructor({
		aircraftId,
		assignedFlightId,
		callSign,
		airlineIcaoCode,
		airlineCallSign,
		arrivalAirport,
		departureAirport,
		aircraftInfo,
		aircraftTypes,
		flightRoutes,
		sectorStore,
		simulatorStore,
	}: {
		aircraftId: string;
		assignedFlightId: string;
		callSign: string;
		airlineIcaoCode: string;
		airlineCallSign: string;
		arrivalAirport: string;
		departureAirport: string;
		aircraftInfo: ObservableMap<string, AircraftInfo>;
		aircraftTypes: ObservableMap<string, AircraftType>;
		flightRoutes: ObservableMap<string, FlightRoute>;
		sectorStore: SectorStore;
		simulatorStore: SimulatorStore;
	}) {
		makeObservable(this, {
			aircraftId: false,
			assignedFlightId: false,
			callSign: false,
			milestoneTargetTimestamp: false,
			aircraftInfo: false,
			aircraftTypes: false,
			flightRoutes: false,
			sectorStore: false,
			simulatorStore: false,
			flightInSectorTimes: observable,
			lastKnownLongitude: observable,
			lastKnownLatitude: observable,
			lastKnownAltitude: observable,
			lastKnownBearing: observable,
			lastKnownSpeed: observable,
			lastKnownVerticalSpeed: observable,
			arrivalAirport: observable,
			departureAirport: observable,
			controlledBy: observable,
			nextSectorController: observable,
			milestoneTargetObjectId: observable,
			lastPassedMilestoneTimestamp: observable,
			lastPassedMilestoneObjectId: observable,
			assignedFlightLevel: observable,
			assignedBearing: observable,
			assignedSpeed: observable,
			predictiveTrajectoryMode: observable,
			predictiveTrajectoryWaypointId: observable,
			predictiveTrajectoryWaypointLatitude: observable,
			predictiveTrajectoryWaypointLongitude: observable,
			localAssignedFlightLevel: observable,
			manualNextSectorFL: observable,
			nextACCFL: observable,
			isNextACCFLManuallySet: observable,
			isNextACCFLFlashing: observable,
			isNextSectorFLFlashing: observable,
			positionHistory: observable,
			transponderCode: observable,
			currentTime: observable,
			routeWaypoints: observable,
			frequency: observable,
			degreased: observable,
			hasCPDLC: observable,

			nextFix: computed,
			nextNav: computed,
			upcomingRouteFixes: computed,
			nextSectorInfo: computed,
			nextSector: computed,
			nextSectorExitPoint: computed,
			nextSectorFL: computed,
			aircraftType: computed,
			wakeTurbulenceCategory: computed,
			speedAndWakeTurbulenceLabel: computed,

			handleTargetReport: action.bound,
			handleTargetMilestone: action.bound,
			handleActualMilestone: action.bound,
			setController: action.bound,
			setAssignedFlightLevel: action.bound,
			setNextSectorController: action.bound,
			setAssignedBearing: action.bound,
			setAssignedSpeed: action.bound,
			setPredictiveTrajectoryRerouted: action.bound,
			setPredictiveTrajectoryReroutedViaWaypoint: action.bound,
			clearPredictiveTrajectoryState: action.bound,
			setLocalAssignedFlightLevel: action.bound,
			setNextSectorFL: action.bound,
			setNextACCFL: action.bound,
			degrease: action.bound,
			undegrease: action.bound,
		});

		this.aircraftId = aircraftId;
		this.assignedFlightId = assignedFlightId;
		this.callSign = callSign;
		this.airlineIcaoCode = airlineIcaoCode;
		this.airlineCallSign = airlineCallSign;
		this.arrivalAirport = arrivalAirport;
		this.departureAirport = departureAirport;
		this.aircraftInfo = aircraftInfo;
		this.aircraftTypes = aircraftTypes;
		this.flightRoutes = flightRoutes;
		this.sectorStore = sectorStore;
		this.simulatorStore = simulatorStore;
	}

	handleTargetReport(targetReport: TargetReportMessage): void {
		// Ignore target reports that are older than the previous one
		// This is because MQTT doesn't guarantie the order of the received messages
		// We could use a MQTT broker that is a message queue with an order, like RabbitMQ
		const {
			time,
			altitude,
			latitude,
			longitude,
			bearing,
			speed,
			verticalSpeed,
		} = targetReport;
		if (!time) {
			throw new Error("Missing time in target report message");
		}
		const timestamp = convertTimestamp(time);
		if (timestamp < this.lastTargetReportTime) {
			return;
		}
		this.lastTargetReportTime = timestamp;
		const altitudeInFlightMeters = convertToFlightMeters(altitude);
		if (altitudeInFlightMeters !== this.lastKnownAltitude) {
			this.lastKnownAltitude = altitudeInFlightMeters;
		}
		if (latitude !== this.lastKnownLatitude) {
			this.lastKnownLatitude = latitude;
			this.positionHistory.unshift({ lat: latitude, lon: longitude });
			if (this.positionHistory.length > 8) {
				this.positionHistory.pop();
			}
		}
		if (longitude !== this.lastKnownLongitude) {
			this.lastKnownLongitude = longitude;
		}

		// Round because we had a lot of invisible updates
		const roundedBearing = Math.round(bearing * 10) / 10;
		if (roundedBearing !== this.lastKnownBearing) {
			this.lastKnownBearing = roundedBearing;
		}
		if (speed !== this.lastKnownSpeed) {
			this.lastKnownSpeed = speed;
		}
		// verticalSpeed may be NaN or missing, default to 0
		const safeVerticalSpeed = Number.isNaN(verticalSpeed) ? 0 : verticalSpeed;
		if (safeVerticalSpeed !== this.lastKnownVerticalSpeed) {
			this.lastKnownVerticalSpeed = safeVerticalSpeed;
		}
	}

	handleTargetMilestone(milestone: FlightMilestonePositionMessage): void {
		const { timeStampSent, position } = milestone;
		if (!timeStampSent) {
			throw new Error("Missing timeStampSent in flight milestone message");
		}
		const timestamp = convertTimestamp(timeStampSent);
		if (timestamp < this.milestoneTargetTimestamp) {
			// Ignore older milestones
			return;
		}
		this.milestoneTargetTimestamp = timestamp;
		if (position) {
			this.milestoneTargetObjectId = position.objectId;
		}
	}

	handleActualMilestone(milestone: FlightMilestonePositionMessage): void {
		const { timeStampSent, position } = milestone;
		if (!timeStampSent || !position?.objectId) {
			return;
		}

		const timestamp = convertTimestamp(timeStampSent);
		if (timestamp < this.lastPassedMilestoneTimestamp) {
			return;
		}

		this.lastPassedMilestoneTimestamp = timestamp;
		this.lastPassedMilestoneObjectId = position.objectId;
	}

	setController(controller: string): void {
		this.controlledBy = controller;
	}

	get nextFix(): string {
		if (this.milestoneTargetObjectId) {
			return this.milestoneTargetObjectId;
		}

		return this.arrivalAirport ?? "UNKNOWN";
	}

	/**
	 * Returns the next navigation point (waypoint) that the aircraft will pass.
	 * This is the first future trajectory point that has a name (objectId).
	 */
	get nextNav(): string {
		const [firstUpcomingFix] = this.upcomingRouteFixes;
		if (firstUpcomingFix) {
			return firstUpcomingFix;
		}

		return this.arrivalAirport ?? "UNKNOWN";
	}

	get upcomingRouteFixes(): string[] {
		const flightRoute = this.flightRoutes.get(this.assignedFlightId);
		if (!flightRoute || flightRoute.trajectory.length === 0) {
			return [];
		}

		return getUpcomingRouteFixes({
			aircraft: this,
			route: flightRoute,
			currentTime: this.simulatorStore.timestamp,
		});
	}

	/**
	 * Returns the next sector info the aircraft will enter based on its trajectory.
	 * Iterates through the flight route waypoints, finds the sector for each,
	 * and returns the first sector that differs from the current sector,
	 * along with the trajectory point where it enters that sector.
	 *
	 * The logic looks at the waypoint just before the current time to determine
	 * the "current" sector, then finds the first waypoint in a different sector.
	 */
	get nextSectorInfo(): NextSectorInfo | undefined {
		const flightRoute = this.flightRoutes.get(this.assignedFlightId);
		if (!flightRoute || flightRoute.trajectory.length === 0) {
			return undefined;
		}

		const trajectories = flightRoute.trajectory;

		// Determine what sector the aircraft is currently in from current position
		const currentSector = this.sectorStore.findSector(
			this.lastKnownLongitude,
			this.lastKnownLatitude,
			this.lastKnownAltitude,
		);
		const currentSectorId = currentSector?.sectorId;

		let startIndex = 0;
		if (this.lastPassedMilestoneObjectId) {
			for (const [i, trajectory] of trajectories.entries()) {
				if (trajectory.objectId === this.lastPassedMilestoneObjectId) {
					startIndex = i + 1;
				}
			}
		}

		for (let i = startIndex; i < trajectories.length; i++) {
			const waypoint = trajectories[i];
			const { longitude, latitude, altitude } = waypoint.trajectoryCoordinate;
			const waypointSector = this.sectorStore.findSector(
				longitude,
				latitude,
				altitude ? convertMetersToFlightLevel(altitude) : undefined,
			);

			if (waypointSector && waypointSector.sectorId !== currentSectorId) {
				return {
					sectorId: waypointSector.sectorId,
					trajectoryPoint: waypoint,
				};
			}
		}

		return undefined;
	}

	/**
	 * Returns the next sector ID the aircraft will enter.
	 * This is a convenience accessor that extracts just the sector ID from nextSectorInfo.
	 */
	get nextSector(): string | undefined {
		return this.nextSectorInfo?.sectorId;
	}

	/**
	 * Returns the exit point name for the current sector (i.e., where the aircraft will exit to the next sector).
	 * Priority:
	 * 1. exitWaypointId from MQTT (FlightEnteringAirspaceMessage) for the current sector
	 * 2. objectId from the nextSectorInfo trajectory point
	 * 3. First named trajectory point after the sector entry point
	 */
	get nextSectorExitPoint(): string | undefined {
		// First, try to get exitWaypointId from MQTT message for the current sector
		const currentSector = this.sectorStore.findSector(
			this.lastKnownLongitude,
			this.lastKnownLatitude,
			this.lastKnownAltitude,
		);
		if (currentSector) {
			const flightInSector = this.flightInSectorTimes.get(
				currentSector.sectorId,
			);
			if (flightInSector?.exitWaypointId) {
				return flightInSector.exitWaypointId;
			}
		}

		// Fallback: use nextSectorInfo trajectory point
		const info = this.nextSectorInfo;
		if (!info) {
			return undefined;
		}

		// If the entry point to the next sector has an ID, use it
		if (info.trajectoryPoint.objectId) {
			return info.trajectoryPoint.objectId;
		}

		// Otherwise, find the next trajectory point after the entry point that has an ID
		const flightRoute = this.flightRoutes.get(this.assignedFlightId);
		if (!flightRoute || flightRoute.trajectory.length === 0) {
			return undefined;
		}

		const trajectories = flightRoute.trajectory;

		const entryIndex = trajectories.indexOf(info.trajectoryPoint);
		if (entryIndex === -1) {
			return undefined;
		}

		const nextNamedPoint = trajectories
			.slice(entryIndex + 1)
			.find((trajectory) => trajectory.objectId);

		return nextNamedPoint?.objectId;
	}

	/**
	 * Returns the flight level for the next sector as a display string.
	 * If manually set, returns the manual value divided by 10 and rounded.
	 * Otherwise, derives it from the trajectory point's altitude at sector entry.
	 * The division by 10 is per app specifications for display.
	 */
	get nextSectorFL(): string {
		// If manually set, return the manual value divided by 10 and rounded
		if (this.manualNextSectorFL !== undefined) {
			return Math.round(this.manualNextSectorFL / 10).toString();
		}

		// Derive from trajectory point altitude
		const info = this.nextSectorInfo;
		if (info) {
			const altitude = info.trajectoryPoint.trajectoryCoordinate.altitude;
			if (altitude !== undefined && altitude > 0) {
				// Convert meters to flight level (altitude in meters -> feet / 100)
				// Then divide by 10 for display per app specifications
				const flightLevel = (altitude * 3.280_84) / 100;
				return Math.round(flightLevel / 10).toString();
			}
		}

		return ""; // Removed NSFL default
	}

	get aircraftType(): string {
		const aircraftInfo = this.aircraftInfo.get(this.aircraftId);
		return aircraftInfo?.aircraftType ?? "";
	}

	handleNewFlightUpdate(newFlight: NewFlightMessage): void {
		const {
			flightUniqueId,
			callSign,
			arrivalAirport,
			departureAirport,
			airlineIcaoCode,
			airlineCallSign,
		} = newFlight;

		this.assignedFlightId = flightUniqueId;
		this.callSign = callSign;
		this.arrivalAirport = arrivalAirport;
		this.departureAirport = departureAirport;
		this.airlineIcaoCode = airlineIcaoCode;
		this.airlineCallSign = airlineCallSign;
	}

	get wakeTurbulenceCategory(): WakeTurbulenceCategory {
		const aircraftInfo = this.aircraftInfo.get(this.aircraftId);
		if (!aircraftInfo) {
			return WakeTurbulenceCategory.WTC_UNKNOWN;
		}

		const { wakeTurbulenceCategory } = aircraftInfo;

		if (wakeTurbulenceCategory !== WakeTurbulenceCategory.WTC_UNKNOWN) {
			return wakeTurbulenceCategory;
		}

		const { aircraftType } = aircraftInfo;
		const aircraftTypeInfo = this.aircraftTypes.get(aircraftType);
		if (!aircraftTypeInfo) {
			return WakeTurbulenceCategory.WTC_UNKNOWN;
		}

		return aircraftTypeInfo.wakeTurbulenceCategory;
	}

	get speedAndWakeTurbulenceLabel(): string {
		const { wakeTurbulenceCategory, lastKnownSpeed } = this;
		const speed = Math.floor(convertMSToKnots(lastKnownSpeed));

		switch (wakeTurbulenceCategory) {
			case WakeTurbulenceCategory.WTC_LIGHT:
				return `L${speed}`;
			case WakeTurbulenceCategory.LOWER_MEDIUM:
			case WakeTurbulenceCategory.UPPER_MEDIUM:
				return `M${speed}`;
			case WakeTurbulenceCategory.LOWER_HEAVY:
			case WakeTurbulenceCategory.UPPER_HEAVY:
			case WakeTurbulenceCategory.JUMBO:
			case WakeTurbulenceCategory.WTC_ALL:
				return `H${speed}`;
			case WakeTurbulenceCategory.WTC_UNKNOWN:
				return `${speed}`;
			default:
				return `${speed}`;
		}
	}
	get last8positions(): Array<{ lat: number; lon: number }> {
		return this.positionHistory;
	}

	handleSectorInFlightMessage(message: FlightEnteringAirspaceMessage): void {
		const {
			sectorId,
			entryPosition,
			exitPosition,
			entryWaypointId,
			exitWaypointId,
		} = message;
		const flightInSector = new FlightInSectorModel({
			sectorId,
			entryPosition,
			exitPosition,
			entryWaypointId,
			exitWaypointId,
		});
		const listsSectorHasFlight = this.flightInSectorTimes;
		if (listsSectorHasFlight) {
			for (const element of listsSectorHasFlight) {
				if (element[1].sectorId === sectorId) {
					element[1] = flightInSector;
					return;
				}
				if (
					element[1]?.entryPosition?.time &&
					entryPosition?.time &&
					element[1]?.entryPosition?.time < entryPosition?.time
				) {
					listsSectorHasFlight.delete(element[0]);
				}
			}
			listsSectorHasFlight.set(sectorId, flightInSector);
		}
	}

	isEnteringFlight(controlledSector: string): boolean {
		const flightEntering = this.flightInSectorTimes.get(controlledSector);
		let flightEnteringTime: Timestamp | undefined;
		if (flightEntering) {
			flightEnteringTime = flightEntering.entryPosition?.time;
		}
		if (
			flightEntering &&
			flightEnteringTime &&
			convertTimestamp(flightEnteringTime) >= this.simulatorStore.timestamp
		) {
			return true;
		}
		return false;
	}

	setAssignedFlightLevel(assignedFlightLevel: string): void {
		this.assignedFlightLevel = assignedFlightLevel;
	}

	setAssignedBearing(assignedBearing: number): void {
		this.assignedBearing = assignedBearing === -1 ? undefined : assignedBearing;
	}

	setAssignedSpeed(assignedSpeed: number): void {
		this.assignedSpeed = assignedSpeed === -1 ? undefined : assignedSpeed;
	}

	setPredictiveTrajectoryRerouted(): void {
		this.predictiveTrajectoryMode = "rerouted";
		this.predictiveTrajectoryWaypointId = undefined;
		this.predictiveTrajectoryWaypointLatitude = undefined;
		this.predictiveTrajectoryWaypointLongitude = undefined;
	}

	setPredictiveTrajectoryReroutedViaWaypoint(
		waypointId: string,
		latitude: number,
		longitude: number,
	): void {
		// Entering direct-to mode: clear heading assignment to avoid stale ACC bearing.
		this.assignedBearing = undefined;
		this.predictiveTrajectoryMode = "rerouted-via-waypoint";
		this.predictiveTrajectoryWaypointId = waypointId;
		this.predictiveTrajectoryWaypointLatitude = latitude;
		this.predictiveTrajectoryWaypointLongitude = longitude;
	}

	clearPredictiveTrajectoryState(): void {
		// Returning to nominal route mode: heading assignment is no longer active.
		this.assignedBearing = undefined;
		this.predictiveTrajectoryMode = "unset";
		this.predictiveTrajectoryWaypointId = undefined;
		this.predictiveTrajectoryWaypointLatitude = undefined;
		this.predictiveTrajectoryWaypointLongitude = undefined;
	}

	setLocalAssignedFlightLevel(localAssignedFlightLevel: string): void {
		this.localAssignedFlightLevel = localAssignedFlightLevel;
	}

	setNextSectorController(nextSectorController: string): void {
		this.nextSectorController = nextSectorController;
	}

	setNextSectorFL(nextSectorFL: string): void {
		const previousValue = this.manualNextSectorFL;
		// "NSFL" is the default/unset value - clear manual setting to use derived value
		if (nextSectorFL === "NSFL") {
			this.manualNextSectorFL = undefined;
		} else {
			const parsed = Number.parseInt(nextSectorFL, 10);
			this.manualNextSectorFL = Number.isNaN(parsed) ? undefined : parsed;
		}

		const hasChanged = previousValue !== this.manualNextSectorFL;
		if (hasChanged) {
			// Clear any existing flash timeout
			if (this.nextSectorFLFlashTimeout) {
				clearTimeout(this.nextSectorFLFlashTimeout);
			}
			// Start flashing
			this.isNextSectorFLFlashing = true;
			// Stop flashing after 3 seconds
			this.nextSectorFLFlashTimeout = setTimeout(
				action(() => {
					this.isNextSectorFLFlashing = false;
				}),
				3000,
			);
		}
	}

	setNextACCFL(nextACCFL: string, isManual = false): void {
		const hasChanged = this.nextACCFL !== nextACCFL;
		this.nextACCFL = nextACCFL;
		// Track if this was a manual change through the interface
		if (isManual) {
			this.isNextACCFLManuallySet = true;
		}

		if (hasChanged) {
			// Clear any existing flash timeout
			if (this.nextACCFLFlashTimeout) {
				clearTimeout(this.nextACCFLFlashTimeout);
			}
			// Start flashing
			this.isNextACCFLFlashing = true;
			// Stop flashing after 1 second
			this.nextACCFLFlashTimeout = setTimeout(
				action(() => {
					this.isNextACCFLFlashing = false;
				}),
				1000,
			);
		}
	}

	/**
	 * Apply a CFL value coming from simulator data.
	 * CFL acts as the initial/authoritative value only until the user manually overrides nextACCFL.
	 */
	applyClearedFlightLevel(clearedFlightLevel: string): void {
		if (this.isNextACCFLManuallySet) {
			return;
		}
		this.setNextACCFL(clearedFlightLevel);
	}

	setPilotRequestFlightLevel(request: string): void {
		this.flightLevelPilotRequest = request;
	}

	setPilotRequestedBearing(bearing: string): void {
		this.bearingPilotRequest = bearing;
	}

	/** Mark this aircraft as degreased (seen/acknowledged). Label becomes normal (not bold). */
	degrease(): void {
		this.degreased = true;
	}

	/** Mark this aircraft as not degreased (not seen). Label becomes bold again. */
	undegrease(): void {
		this.degreased = false;
	}
}
